import type { KarteRoleScope } from '../contract/facts.js';
import type { QantProductKey } from '../contract/qant.js';

/**
 * メニュー (L1 product / L2 item / popover item) の表示条件を表す declarative な述語ツリー。
 *
 * v5 (`navigation-v5`) は front で事実から事前評価した `filters: boolean[]` (AND) +
 * `productContractsFilter` (条件ツリー) + `isAdminOnly` でメニューを出し分けていた。
 * v2 では「事実は各基盤の NaviInput で渡し、述語は本パッケージに単一配置する」方針 (計画 §3) のため、
 * 各メニューに本 condition を宣言的に持たせ、`filterProducts` が `NaviFacts` に対して評価する。
 *
 * 評価規則 (`filter/evaluateCondition.ts`):
 * - Karte 系述語 (`facts.karte` を参照):
 *   - `role`     : `facts.karte.roles[scope]` に `level` が含まれる (v5 `permission.hasRole(scope, level)`)
 *   - `option`   : `facts.karte.options[product][option] === true` (v5 `productContractsFilter` の option 条件)
 *   - `plugin`   : `facts.karte.plugins[plugin] === true` (v5 `permission.hasInstalledPlugin(plugin)`)
 *   - `i18nScope`: `!facts.karte.i18n.isGlobalEnterpriseScope || requiredScope` (v5 `i18nFilters`。ja は常時表示)
 * - QANT 系述語 (`facts.qant` を参照):
 *   - `qantProductAdmin`: `facts.qant.productAdmin[product] === true` (v1 `HasProduct管理権限(product)` 相当)
 *   - `qantProductPii`  : `facts.qant.productPii[product] === true` (v1 `HasPiiAccessNow(product)` 相当)
 *   - `employee`        : `facts.qant.isEmployee === true` (v1 ナビの社内メンバー限定表示に相当)
 *   - `qantSmsEnabled`  : `facts.qant.qantSmsEnabled === true` (QANT Project `featureFlags.qantSmsEnabled`)
 * - `and` / `or` / `not`: ブール演算
 *
 * ある platform の facts が与えられていない場合、その platform の述語を含む condition は
 * ツリーごと評価がスキップされる (= 表示。`filterProducts.passesCondition` 参照)。
 * platform 混在 condition (例 `and(role(...), employee())`) は片側の facts 欠落でも
 * 丸ごとスキップされるため、意図しない非表示には倒れない。
 */
export type NaviCondition =
  | { kind: 'role'; scope: KarteRoleScope; level: string }
  | { kind: 'option'; product: string; option: string }
  | { kind: 'plugin'; plugin: string }
  | { kind: 'i18nScope'; requiredScope: boolean }
  | { kind: 'qantProductAdmin'; product: QantProductKey }
  | { kind: 'qantProductPii'; product: QantProductKey }
  | { kind: 'employee' }
  | { kind: 'qantSmsEnabled' }
  | { kind: 'and'; conditions: readonly NaviCondition[] }
  | { kind: 'or'; conditions: readonly NaviCondition[] }
  | { kind: 'not'; condition: NaviCondition };

// --- 可読性のための builder ヘルパ (メニュー定義側で使う) ---

export const role = (scope: KarteRoleScope, level: string): NaviCondition => ({ kind: 'role', scope, level });
export const option = (product: string, opt: string): NaviCondition => ({ kind: 'option', product, option: opt });
export const plugin = (id: string): NaviCondition => ({ kind: 'plugin', plugin: id });
/** v5 `i18nFilters({ isGlobalEnterpriseScope })` 相当。ja プロジェクトでは常に true。 */
export const i18nScope = (requiredScope: boolean): NaviCondition => ({ kind: 'i18nScope', requiredScope });
/** v1 `HasProduct管理権限(product)` 相当 (systemAdmin または PJメンバー + group policy admin)。 */
export const qantProductAdmin = (product: QantProductKey): NaviCondition => ({ kind: 'qantProductAdmin', product });
/** v1 `HasPiiAccessNow(product)` 相当 (一時 PII グラント または group policy の product 別 PII 権限)。 */
export const qantProductPii = (product: QantProductKey): NaviCondition => ({ kind: 'qantProductPii', product });
/** v1 `account.isEmployee` (社内アカウント限定) 相当。 */
export const employee = (): NaviCondition => ({ kind: 'employee' });
/** QANT Project `featureFlags.qantSmsEnabled === true` のときのみ表示。 */
export const qantSmsEnabled = (): NaviCondition => ({ kind: 'qantSmsEnabled' });
export const and = (...conditions: NaviCondition[]): NaviCondition => ({ kind: 'and', conditions });
export const or = (...conditions: NaviCondition[]): NaviCondition => ({ kind: 'or', conditions });
export const not = (condition: NaviCondition): NaviCondition => ({ kind: 'not', condition });
